import { useEffect, useState } from "react";
import type { CreditActivity } from "api";
import { creditsApi } from "api";
import { Select } from "components/ui/Select";
import { cubeUrl } from "constants/products.constants";
import { formatMonth } from "utils/date.utils";
import { isUnauthenticated } from "utils/error.utils";
import { formatCount } from "utils/number.utils";
import { formatCredits, formatUnits, monthOption } from "./credits.model";

type CreditActivityCardProps = {
  /** Bumped by the pane when the balance moves, so the month reads again. */
  version: number;
  onSessionLost: () => void;
};

/**
 * `.acm`: what the company spent each month, by product, and its top-ups.
 * The month picker reaches back to the first month with activity, at most 36.
 */
export const CreditActivityCard = ({ version, onSessionLost }: CreditActivityCardProps) => {
  const [month, setMonth] = useState<string | null>(null);
  const [activity, setActivity] = useState<CreditActivity | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    creditsApi
      .activity(month ?? undefined)
      .then(({ data }) => {
        if (cancelled) return;
        setActivity(data);
        setFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isUnauthenticated(error)) {
          onSessionLost();
          return;
        }
        setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [month, version, onSessionLost]);

  const name = activity === null ? "" : formatMonth(activity.month);
  const top = activity?.products[0]?.credits ?? 1;
  const toppedUp = activity?.top_ups.reduce((sum, topUp) => sum + topUp.credits, 0) ?? 0;

  return (
    <div className="pcx acm">
      <div className="pcx__top">
        <div>
          <div className="pcx__h">QCredits activity</div>
          <p className="pcx__sub">
            {activity === null
              ? "Spent by product each month"
              : activity.spent > 0
                ? `${formatCredits(activity.spent)} spent in ${name}`
                : `No QCredits spent in ${name}`}
          </p>
        </div>
        {activity !== null && (
          <div className="acm__sel">
            <Select
              ariaLabel="Month"
              value={activity.month}
              options={activity.months.map(monthOption)}
              onChange={setMonth}
            />
          </div>
        )}
      </div>

      {failed && activity === null && (
        <p className="pc-note pc-note--bad">Could not load your QCredits activity. Please refresh the page.</p>
      )}

      {activity !== null && (
        <div className="acm__rows">
          {activity.products.length === 0 ? (
            <p className="acm__none">Nothing was spent this month.</p>
          ) : (
            activity.products.map((row) => (
              <div className="acm__r" key={row.product ?? row.name}>
                {row.cube === null ? <span /> : <img src={cubeUrl(row.cube)} alt="" width={32} height={32} />}
                <span className="acm__n">
                  <b>{row.name}</b>
                  <em>{formatUnits(row.units, row.unit)}</em>
                </span>
                <span className="acm__bar">
                  <i style={{ width: `${Math.max(3, (row.credits / top) * 100)}%` }} />
                </span>
                <span className="acm__v">{formatCredits(row.credits)}</span>
              </div>
            ))
          )}
        </div>
      )}

      {toppedUp > 0 && (
        <p className="acm__top">
          Top-ups this month: <b>+{formatCount(toppedUp)} QCredits</b>
        </p>
      )}
    </div>
  );
};
